import React, { useMemo, useState } from 'react';
import {
  BatchRowDTO,
  TraineeItemDTO,
  assessmnetsApi,
  AttemptSummaryDTO,
  AttemptLiteDTO,
  PostUnlockStatusResponse,
} from '../api';
import ModalPortal from './ModalPortal';

const toLocalDateTimeInput = (iso?: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - tzOffset * 60000);
  return local.toISOString().slice(0, 16);
};

const formatDisplayDateTime = (iso?: string | null) => {
  if (!iso) return 'Not set';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString();
};

const formatScoreDisplay = (score: unknown) => {
  if (score === null || score === undefined) return '--';
  const numeric = typeof score === 'number' ? score : Number(score);
  if (!Number.isFinite(numeric)) return '--';
  return numeric.toFixed(1);
};

const POST_ATTENDANCE_THRESHOLD = 70;

export default function AssessmentsAdmin() {
  const [rows, setRows] = React.useState<BatchRowDTO[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [batchStatus, setBatchStatus] = React.useState('');
  const [onlyOpen, setOnlyOpen] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const [traineeQ, setTraineeQ] = useState('');

  const [selectedBatchId, setSelectedBatchId] = React.useState<number | null>(null);
  const selectedBatch = React.useMemo(
    () => rows.find(b => b.batch_id === selectedBatchId) ?? null,
    [rows, selectedBatchId]
  );

  const [unlockStatus, setUnlockStatus] = useState<PostUnlockStatusResponse | null>(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlockInputValue, setUnlockInputValue] = useState('');
  const [savingUnlock, setSavingUnlock] = useState(false);
  const [unlockSuccessMessage, setUnlockSuccessMessage] = useState<string | null>(null);
  const [showUnlockForm, setShowUnlockForm] = useState(false);

  // Memoize the filtered trainees
  const filteredTrainees = useMemo(() => {
    if (!selectedBatch) return [];
    if (!traineeQ) return selectedBatch.trainees;
    const lowerCaseQ = traineeQ.toLowerCase();
    return selectedBatch.trainees.filter(trainee =>
      trainee.trainee.name.toLowerCase().includes(lowerCaseQ) ||
      trainee.trainee.email.toLowerCase().includes(lowerCaseQ) ||
      trainee.trainee.phone.toLowerCase().includes(lowerCaseQ)
    );
  }, [selectedBatch, traineeQ]);

  const handleUnlockSave = async () => {
    if (!selectedBatch || !unlockStatus) return;
    setSavingUnlock(true);
    setUnlockError(null);
    setUnlockSuccessMessage(null);
    try {
      const payload =
        unlockInputValue && unlockInputValue.trim().length > 0
          ? { scheduled_datetime: unlockInputValue }
          : {};
      const response = await assessmnetsApi.updatePostUnlock(selectedBatch.batch_id, payload);
      if (response.success && response.data) {
        const data = response.data;
        setUnlockStatus(data);
        setUnlockInputValue(
          toLocalDateTimeInput(data.actual_datetime ?? data.planned_datetime)
        );
        setUnlockSuccessMessage('Post-assessment schedule updated and eligible trainees auto-assigned/notified.');
        setShowUnlockForm(false);
      } else {
        setUnlockError(response.message ?? 'Failed to update post-assessment schedule.');
      }
    } catch (err) {
      setUnlockError('Failed to update post-assessment schedule.');
    } finally {
      setSavingUnlock(false);
    }
  };

  React.useEffect(() => {
    if (!selectedBatch) {
      setUnlockStatus(null);
      setUnlockInputValue('');
      setUnlockError(null);
      setUnlockSuccessMessage(null);
      setShowUnlockForm(false);
      return;
    }

    let cancelled = false;
    const fetchStatus = async () => {
      setUnlockLoading(true);
      setUnlockError(null);
      setUnlockSuccessMessage(null);
      setShowUnlockForm(false);
      try {
        const response = await assessmnetsApi.getPostUnlockStatus(selectedBatch.batch_id);
        if (cancelled) return;
        if (response.success && response.data) {
          const data = response.data;
          setUnlockStatus(data);
          setUnlockInputValue(
            toLocalDateTimeInput(data.actual_datetime ?? data.planned_datetime)
          );
        } else {
          setUnlockStatus(null);
          setUnlockInputValue('');
          setUnlockError(response.message ?? 'Failed to load post-assessment status.');
        }
      } catch (err) {
        if (!cancelled) {
          setUnlockStatus(null);
          setUnlockInputValue('');
          setUnlockError('Failed to load post-assessment status.');
        }
      } finally {
        if (!cancelled) {
          setUnlockLoading(false);
        }
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [selectedBatch]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await assessmnetsApi.getAdminAssessments({
        q: selectedBatch ? undefined : (q || undefined),
        batchStatus: selectedBatch ? undefined : (batchStatus || undefined),
        onlyOpen: onlyOpen || undefined,
        page,
        pageSize,
        sort: 'recent',
        includeHistory: true,
        historyLimit: 2,
      });
      if (response.success && response.data) {
        setRows(response.data.rows);
        setTotal(response.data.total);
      }
    } finally {
      setLoading(false);
    }
  }, [q, batchStatus, onlyOpen, page]);

  React.useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    const response = await assessmnetsApi.exportAdminAssessments({
      q: q || undefined,
      batchStatus: batchStatus || undefined,
      onlyOpen: onlyOpen || undefined,
    });
    if (response.status === 200 && response.data) {
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'assessments.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const renderPostUnlockCard = () => {
    if (!selectedBatch) return null;

    const attendance = unlockStatus?.attendance;
    const attendanceComplete = attendance?.complete ?? false;
    const isUnlocked = Boolean(unlockStatus?.actual_datetime);
    const canEditNow = Boolean(unlockStatus?.allowed_for_user);
    const showButton = canEditNow && !showUnlockForm;
    const hasLimitedEditBeenUsed = unlockStatus ? !unlockStatus.can_ca_mp_edit : false;
    const buttonLabel = hasLimitedEditBeenUsed && isUnlocked ? 'Update Schedule' : 'Unlock Post Assessment';

    return (
      <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Post-Assessment Unlock</h3>
            <p className="text-sm text-gray-500">
              Unlocking lets you confirm or adjust the final post-assessment slot.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Saving also auto-assigns post assessments to trainees with ≥ 70% attendance (excluding launchpad sessions) and sends the usual trainee/stakeholder email alerts.
            </p>
          </div>
        </div>

        {unlockLoading && (
          <p className="mt-3 text-sm text-gray-500">Checking unlock status…</p>
        )}
        {unlockError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {unlockError}
          </div>
        )}
        {unlockSuccessMessage && (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
            {unlockSuccessMessage}
          </div>
        )}

        {unlockStatus && (
          <>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <InfoCard label="Scheduled (proposal)" value={formatDisplayDateTime(unlockStatus.planned_datetime)} />
              <InfoCard label="Final schedule" value={formatDisplayDateTime(unlockStatus.actual_datetime)} />
              <InfoCard label="Window ends" value={formatDisplayDateTime(unlockStatus.end_datetime)} />
            </div>

            <div className="mt-4">
              <div className="text-sm font-medium text-gray-800">Attendance status</div>
              <p className="text-sm text-gray-600 mt-1">
                {attendanceComplete
                  ? 'Attendance is complete for all scheduled sessions.'
                  : 'Cannot unlock since attendance is not filled for all the sessions.'}
              </p>
            </div>

            {unlockStatus.updated_by && (
              <p className="mt-3 text-xs text-gray-500">
                Last updated by{' '}
                <span className="font-medium text-gray-700">
                  {unlockStatus.updated_by.full_name || unlockStatus.updated_by.user_id}
                </span>{' '}
                on {formatDisplayDateTime(unlockStatus.updated_at)}
              </p>
            )}

            {!unlockStatus.allowed_for_user && unlockStatus.blocked_reason && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                {unlockStatus.blocked_reason}
              </div>
            )}

            {showButton && (
              <button
                onClick={() => {
                  setShowUnlockForm(true);
                  setUnlockInputValue(
                    toLocalDateTimeInput(unlockStatus.actual_datetime ?? unlockStatus.planned_datetime)
                  );
                }}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                {buttonLabel}
              </button>
            )}

            {showUnlockForm && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post-assessment date & time
                </label>
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <input
                    type="datetime-local"
                    value={unlockInputValue}
                    onChange={(e) => setUnlockInputValue(e.target.value)}
                    className="w-full md:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleUnlockSave}
                      disabled={savingUnlock}
                      className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {savingUnlock ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setShowUnlockForm(false);
                        setUnlockError(null);
                      }}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  We prefill the current schedule. Adjust it only if you need to shift the post-assessment slot.
                </p>
              </div>
            )}

            {!isUnlocked && !canEditNow && (
              <p className="mt-4 text-sm text-gray-500">
                Unlock will be available once the pending conditions are resolved.
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  // --- Trainee View ---
  const renderTraineeView = () => {
    const b = selectedBatch!;
    const preWindow = b.trainees[0]?.pre?.window;
    const postWindow = b.trainees[0]?.post?.window;
    return (
      <>
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-900 leading-tight">
            Trainees for: <span className="text-indigo-600">{b.batch_code}</span>
          </h2>
          <button
            onClick={() => { setSelectedBatchId(null); setPage(1); }}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors duration-200"
            aria-label="Back to Batches" // Added ARIA label for accessibility
          >
            {/* Back Arrow SVG */}
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Back to Batches
          </button>
        </div>

        {renderPostUnlockCard()}

        {/* Common Info Section */}
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <InfoCard label="Program Name" value={b.program_name || 'N/A'} />
            <InfoCard label="Mobilisation Partner" value={b.mp_name || 'N/A'} />
            <InfoCard label="Trainer Name" value={b.trainer_name || 'N/A'} />
            <InfoCard label="Center Name" value={b.center_name || 'N/A'} />
            {/* <InfoCard label="Trainee Count" value={b.trainee_count} /> */}
            <InfoCard label="Center Admin" value={b.ca_name || 'N/A'} />
          </div>
        </div>

        {/* Updated KPI strip with icons */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-4 mb-6">
          <KPI label="Total Enrolled Trainees" value={b.trainee_count} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.914A8.567 8.567 0 0010 17a8.567 8.567 0 004.546-2.086A5 5 0 0010 11z" clipRule="evenodd" /></svg>} />
          <KPI label="Pre Completion" value={`${b.kpis.conversion.pre.completionRatePct}%`} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>} />
          <KPI label="Post Completion" value={`${b.kpis.conversion.post.completionRatePct}%`} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>} />
          {/* <KPI label="Avg Improvement" value={
            b.kpis.performance.improvement.avgDelta != null ? `${b.kpis.performance.improvement.avgDelta}` : '—'
          } icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} /> */}
          <KPI label="Open Windows" value={`${b.kpis.windows.pre.openCount}/${b.kpis.windows.post.openCount}`} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>} />
        </div>

        {/* Centralized Assessment Window Display */}
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Pre-Assessment Window</div>
              {preWindow && preWindow.start_at && preWindow.end_at ? (
                <div className="text-gray-900 font-semibold text-lg">
                  {new Date(preWindow.start_at).toLocaleString()}
                  <span className="text-gray-500 font-normal mx-1">—</span>
                  {new Date(preWindow.end_at).toLocaleString()}
                </div>
              ) : (
                <div className="text-gray-400">Not available</div>
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Post-Assessment Window</div>
              {postWindow && postWindow.start_at && postWindow.end_at ? (
                <div className="text-gray-900 font-semibold text-lg">
                  {new Date(postWindow.start_at).toLocaleString()}
                  <span className="text-gray-500 font-normal mx-1">—</span>
                  {new Date(postWindow.end_at).toLocaleString()}
                </div>
              ) : (
                <div className="text-gray-400">Not available</div>
              )}
            </div>
          </div>
        </div>

        {/* New search bar for trainees */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 md:mb-8">
          <div className="flex-1">
            <label htmlFor="trainee-search-input" className="block text-sm font-medium text-gray-700 mb-1">Search Trainees</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.817A6 6 0 012 8z" clipRule="evenodd" /></svg>
              </div>
              <input
                id="trainee-search-input"
                type="text"
                className="block w-full rounded-lg border-gray-300 shadow-sm pl-10 pr-4 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Search by name, email, or phone"
                value={traineeQ}
                onChange={e => setTraineeQ(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingCard text="Loading trainees..." />
        ) : filteredTrainees.length === 0 ? (
          <EmptyCard text="No trainees found for this batch or matching your search." />
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Trainee Name</Th>
                  <Th>Contact</Th>
                  <Th>Attendance</Th>
                  <Th>Pre-Assessment</Th>
                  <Th>Post-Assessment</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTrainees.map((row: TraineeItemDTO)=> (
                  <tr key={row.trainee.trainee_id}>
                    <Td className="font-medium text-gray-900">{row.trainee.name}</Td>
                    <Td className="text-gray-500">
                      <div>{row.trainee.email}</div>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span>{row.attendance_percentage?.toFixed(1)}%</span>
                        {/* {row.attendance_percentage < (b.kpis.attendance.thresholdPct || 75) && (
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">Below Threshold</span>
                        )} */}
                      </div>
                    </Td>
                    <Td><AttemptSummary attempt={row.pre} history={row.pre_history} /></Td>
                    <Td>
                      {row.attendance_percentage < POST_ATTENDANCE_THRESHOLD ? (
                        <div className="text-sm font-medium text-amber-600">
                          Not eligible (attendance below {POST_ATTENDANCE_THRESHOLD}%)
                        </div>
                      ) : (
                        <AttemptSummary attempt={row.post} history={row.post_history} />
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  // --- Batch View ---
  const renderBatchView = () => {

    const getStatusByWindow = (window: { start_at: string | null; end_at: string | null } | null) => {
      if (!window || !window.start_at || !window.end_at) {
        return 'N/A';
      }
      const now = new Date().getTime();
      const start = new Date(window.start_at).getTime();
      const end = new Date(window.end_at).getTime();
      if (now < start) {
        return 'Pending';
      }
      if (now >= start && now <= end) {
        return 'Started';
      }
      if (now > end) {
        return 'Ended';
      }
      return 'N/A';
    };

    const StatusBadge = ({ status }: { status: string }) => {
      let colorClass = '';
      let icon = null;
      switch (status) {
        case 'Pending':
          colorClass = 'bg-yellow-100 text-yellow-800';
          icon = <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 10-2 0v2H6a1 1 0 100 2h1v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" /></svg>;
          break;
        case 'Started':
          colorClass = 'bg-blue-100 text-blue-800';
          icon = <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 00-2 0v2a1 1 0 102 0V7zm2 0a1 1 0 00-2 0v4a1 1 0 102 0V7zM7 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" /></svg>;
          break;
        case 'Ended':
          colorClass = 'bg-green-100 text-green-800';
          icon = <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;
          break;
        default:
          colorClass = 'bg-gray-100 text-gray-500';
          break;
      }

      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
          {icon} {status}
        </span>
      );
    };

    return (
      <>
        <header className="mb-6 md:mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 leading-tight">Assessments Dashboard</h1>
          <p className="mt-1 text-base text-gray-500">View and manage batches and export data.</p>
        </header>

        {/* Filters and Actions */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6">
            <div className="flex-1">
              <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 mb-1">Search Batches</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.817A6 6 0 012 8z" clipRule="evenodd" /></svg>
                </div>
                <input
                  id="search-input"
                  type="text"
                  className="block w-full rounded-lg border-gray-300 shadow-sm pl-10 pr-4 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Batch Code / Trainee / Phone / Email"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
              </div>
            </div>

            <div className="w-full sm:w-auto">
              <label htmlFor="batch-status" className="block text-sm font-medium text-gray-700 mb-1">Batch status</label>
              <select
                id="batch-status"
                className="block w-full rounded-lg border-gray-300 shadow-sm pr-10 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={batchStatus}
                onChange={e => setBatchStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="InProgress">In Progress</option>
                <option value="InPlacement">In Placement</option>
                <option value="PostPlacement">Post Placement</option>
                <option value="Completed">Completed (Post-Assessment Ended)</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {/* <label className="flex items-center gap-2">
              <input type="checkbox" checked={onlyOpen} onChange={e => setOnlyOpen(e.target.checked)} />
              <span className="text-sm">Only open windows</span>
            </label> */}

            <button
              onClick={onExport}
              className="w-full sm:w-auto mt-4 sm:mt-0 inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" /></svg>
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingCard text="Loading batches..." />
        ) : rows.length === 0 ? (
          <EmptyCard text="No batches found. Try adjusting your filters." />
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Batch Code</Th>
                  <Th>Program</Th>
                  <Th>Status</Th>
                  <Th>Pre-Assessment Status</Th>
                  <Th>Post-Assessment Status</Th>
                  {/* <Th>Attendance Issues</Th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((b) => {
                  const preStatus = getStatusByWindow(b.trainees[0]?.pre?.window);
                  const postStatus = getStatusByWindow(b.trainees[0]?.post?.window);
                  return (<tr
                    key={b.batch_id}
                    onClick={() => setSelectedBatchId(b.batch_id)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <Td className="font-medium text-gray-900">{b.batch_code}</Td>
                    <Td className="text-gray-500">{b.program_name ?? '—'}</Td>
                    <Td><Badge status={b.status} /></Td>
                    <Td><StatusBadge status={preStatus} /></Td>
                    <Td><StatusBadge status={postStatus} /></Td>
                    {/* <Td className="text-gray-700">
                    <span className={b.kpis.attendance.belowThresholdCount > 0 ? "text-red-600 font-semibold" : ""}>
                      {b.kpis.attendance.belowThresholdCount}
                    </span>
                  </Td> */}
                  </tr>)
                }
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination total={total} page={page} pageSize={pageSize} onPageChange={handlePageChange} />
      </>
    )
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {selectedBatch ? renderTraineeView() : renderBatchView()}
      </div>
    </div>
  );
}

// --- Reusable Components ---

function InfoCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-6 py-4 whitespace-nowrap text-sm ${className}`}>{children}</td>;
}

function LoadingCard({ text }: { text: string }) {
  return (
    // <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">
    //   <svg className="animate-spin h-8 w-8 mx-auto text-indigo-500" viewBox="0 0 24 24">
    //     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    //     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    //   </svg>
    //   <p className="mt-4 text-lg">{text}</p>
    // </div>
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
}
function EmptyCard({ text }: { text: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
      <p className="text-xl font-medium text-gray-500">{text}</p>
    </div>
  );
}

// Updated KPI component to accept an icon prop
function KPI({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white shadow-sm p-4 flex flex-col items-center sm:flex-row sm:items-center gap-4 text-center sm:text-left">
      <div className="flex-shrink-0 bg-gray-100 rounded-full p-2">
        {icon}
      </div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-lg font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );
}

// Modal component for viewing attempt history
function HistoryModal({ title, history, onClose }: { title: string; history: AttemptLiteDTO[]; onClose: () => void }) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 m-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {history.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {history.map((attempt, index) => (
              <li key={index} className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">
                    Attempt {history.length - index}
                  </div>
                  <Badge status={attempt.status} />
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-semibold text-gray-700">Score: {formatScoreDisplay(attempt.score)}</span>
                  <br />
                  <span className="text-gray-600">Started: {new Date(attempt.started_at).toLocaleString()}</span>
                  {attempt.provider_attempt_id && (
                    <div className="mt-1">
                      <span className="text-gray-400 text-xs">Provider ID: {attempt.provider_attempt_id}</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No history found for this assessment.</p>
        )}
        </div>
      </div>
    </ModalPortal>
  );
}

// Updated AttemptSummary with a modal trigger
function AttemptSummary({ attempt, history }: { attempt: AttemptSummaryDTO; history?: AttemptLiteDTO[] }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const hasHistory = (history?.length || 0) > 0;

  const formatScore = (score: unknown) => formatScoreDisplay(score);

  const BadgeMini = ({ on, label, cls }: { on: boolean; label: string; cls: string }) =>
    on ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span> : null;

  if (!attempt?.assigned && !attempt?.taken) {
    return <span className="text-gray-400">Not Assigned</span>;
  }

  const hasIntegrityIssues = attempt.result?.status === 'COMPLETED' && attempt.result.provider_attempt_id !== null && attempt.window !== null;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        <BadgeMini on={!!attempt.assigned} label="Assigned" cls="bg-green-100 text-green-800" />
        <BadgeMini on={!!attempt.open} label="Open" cls="bg-blue-100 text-blue-800" />
        <BadgeMini on={!!attempt.taken} label="Taken" cls="bg-purple-100 text-purple-800" />
        <BadgeMini on={!!attempt.locked} label="Locked" cls="bg-red-100 text-red-800" />
        {/* {hasIntegrityIssues && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.515C8.922 2.146 11.08 2.146 11.743 3.515l.394.79c.148.296.446.475.772.475h.853a1 1 0 011 .876l.123 1.233a1 1 0 01-.176.877l-.634.725a1 1 0 010 1.348l.634.725a1 1 0 01.176.877l-.123 1.233a1 1 0 01-1 .876h-.853c-.326 0-.624.179-.772.475l-.394.79c-.663 1.37-2.821 1.37-3.486 0l-.394-.79a1 1 0 01-.772-.475h-.853a1 1 0 01-1-.876l-.123-1.233a1 1 0 01.176-.877l.634-.725a1 1 0 010-1.348l-.634-.725a1 1 0 01-.176-.877l.123-1.233a1 1 0 011-.876h.853c.326 0 .624-.179.772-.475l.394-.79z" clipRule="evenodd" /><path fillRule="evenodd" d="M10 12a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
            Integrity Flag
          </span>
        )} */}
        {hasHistory && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 focus:outline-none"
            aria-label="View history"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.5 2.5a1 1 0 001.414-1.414L11 9.586V6z" clipRule="evenodd" /></svg>
            <span className="text-[11px] font-medium">History</span>
          </button>
        )}
      </div>
      <div className="text-xs text-gray-600">
        Score: <strong className="text-gray-900 text-sm font-semibold">{formatScore(attempt.result ? attempt.result.score : null)}</strong>
        {attempt.result?.status ? ` • ${attempt.result.status}` : ''}
      </div>
      <div className="text-[11px] text-gray-500">
        {attempt.result?.completed_at
          ? `Completed: ${new Date(attempt.result.completed_at).toLocaleString()}`
          : attempt.result?.started_at
            ? `Started: ${new Date(attempt.result.started_at).toLocaleString()}`
            : 'Not started'}
      </div>
      {isModalOpen && (
        <HistoryModal
          title={`Attempt History`}
          history={history || []}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

function Badge({ status }: { status: string }) {
  let colorClass = '';
  switch (status) {
    case 'InProgress': colorClass = 'bg-blue-100 text-blue-800'; break;
    case 'Completed': colorClass = 'bg-green-100 text-green-800'; break;
    case 'Draft': colorClass = 'bg-gray-100 text-gray-500'; break;
    case 'Cancelled': colorClass = 'bg-red-100 text-red-800'; break;
    default: colorClass = 'bg-gray-100 text-gray-500';
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {status}
    </span>
  );
}

interface PaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}
function Pagination({ total, page, pageSize, onPageChange }: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="mt-8 flex justify-between items-center bg-white p-4 rounded-2xl shadow-sm">
      <span className="text-sm font-medium text-gray-700">
        Page {page} of {totalPages} ({total} records)
      </span>
      <div className="flex space-x-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page * pageSize >= total}
          className="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
